import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { CameraViewId } from '../core/types.ts';

interface CameraPreset {
  position: THREE.Vector3;
  target: THREE.Vector3;
}

export class CameraController {
  private camera: THREE.PerspectiveCamera;
  private controls: OrbitControls;
  private currentViewId: CameraViewId = 'white';
  private previousViewId: CameraViewId = 'white';

  // Smooth transition interpolation
  private isTransitioning = false;
  private transitionProgress = 0;
  private transitionDuration = 0.8; // seconds
  private startPosition: THREE.Vector3 = new THREE.Vector3();
  private targetPosition: THREE.Vector3 = new THREE.Vector3();
  private startTarget: THREE.Vector3 = new THREE.Vector3();
  private targetLookAt: THREE.Vector3 = new THREE.Vector3();

  // Temporary focus restore timer
  private focusTimeout: number | null = null;

  private presets: Record<CameraViewId, CameraPreset> = {
    top: {
      position: new THREE.Vector3(0, 24, 0.05),
      target: new THREE.Vector3(0, 0, 0)
    },
    isometric: {
      position: new THREE.Vector3(13, 15, 15),
      target: new THREE.Vector3(0, 0, 0)
    },
    cinematic: {
      position: new THREE.Vector3(-10, 5, 12),
      target: new THREE.Vector3(0, 1.2, 0)
    },
    white: {
      position: new THREE.Vector3(0, 11, 14),
      target: new THREE.Vector3(0, 0.2, 0)
    },
    black: {
      position: new THREE.Vector3(0, 11, -14),
      target: new THREE.Vector3(0, 0.2, 0)
    },
    free: {
      position: new THREE.Vector3(10, 12, 14),
      target: new THREE.Vector3(0, 0, 0)
    },
    focus: {
      position: new THREE.Vector3(4, 6, 6),
      target: new THREE.Vector3(0, 0, 0)
    }
  };

  constructor(camera: THREE.PerspectiveCamera, domElement: HTMLElement) {
    this.camera = camera;
    this.controls = new OrbitControls(this.camera, domElement);

    // Damping and limits
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.08;
    this.controls.minDistance = 4;
    this.controls.maxDistance = 35;
    this.controls.maxPolarAngle = Math.PI / 2 - 0.04; // Don't clip through floor
    this.controls.minPolarAngle = 0.05;

    // Detect user manual orbit to switch view ID to 'free'
    this.controls.addEventListener('start', () => {
      if (!this.isTransitioning) {
        this.currentViewId = 'free';
      }
    });

    // Set initial position
    this.setView('white', true);
  }

  public getControls(): OrbitControls {
    return this.controls;
  }

  public getCurrentViewId(): CameraViewId {
    return this.currentViewId;
  }

  public setView(viewId: CameraViewId, instant = false): void {
    if (viewId === 'focus') return; // use focusOnTarget for focus view
    this.previousViewId = this.currentViewId;
    this.currentViewId = viewId;

    const preset = this.presets[viewId];
    if (instant) {
      this.isTransitioning = false;
      this.camera.position.copy(preset.position);
      this.controls.target.copy(preset.target);
      this.controls.update();
      return;
    }

    this.startTransition(preset.position, preset.target, 0.7);
  }

  public focusOnTarget(targetPos: THREE.Vector3, durationMs = 1200): void {
    if (this.currentViewId !== 'focus') {
      this.previousViewId = this.currentViewId;
    }
    this.currentViewId = 'focus';

    if (this.focusTimeout) {
      window.clearTimeout(this.focusTimeout);
    }

    // Offset camera slightly up and back from target piece
    const offset = new THREE.Vector3(3.5, 4.5, 5.5);
    const camPos = targetPos.clone().add(offset);

    this.startTransition(camPos, targetPos, 0.4);

    // Return to previous view after action
    this.focusTimeout = window.setTimeout(() => {
      this.setView(this.previousViewId);
      this.focusTimeout = null;
    }, durationMs);
  }

  public rotate180(): void {
    const currentTarget = this.controls.target.clone();
    const currentPos = this.camera.position.clone();
    // Rotate 180 deg around Y
    const newPos = new THREE.Vector3(-currentPos.x, currentPos.y, -currentPos.z);
    this.startTransition(newPos, currentTarget, 0.8);
  }

  public resetCamera(): void {
    this.setView(this.currentViewId === 'black' ? 'black' : 'white');
  }

  private startTransition(destPos: THREE.Vector3, destTarget: THREE.Vector3, duration = 0.7): void {
    this.isTransitioning = true;
    this.transitionProgress = 0;
    this.transitionDuration = duration;

    this.startPosition.copy(this.camera.position);
    this.targetPosition.copy(destPos);

    this.startTarget.copy(this.controls.target);
    this.targetLookAt.copy(destTarget);

    this.controls.enabled = false;
  }

  public update(delta: number): void {
    if (this.isTransitioning) {
      this.transitionProgress += delta / this.transitionDuration;
      const t = Math.min(1, this.transitionProgress);

      // Smooth easeInOutCubic
      const ease = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

      this.camera.position.lerpVectors(this.startPosition, this.targetPosition, ease);
      this.controls.target.lerpVectors(this.startTarget, this.targetLookAt, ease);

      if (t >= 1) {
        this.isTransitioning = false;
        this.controls.enabled = true;
      }
    }

    this.controls.update();
  }
}
